import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { useRepairShopContext } from "./RepairShopContext";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Invoice,
  InvoiceFormData,
  Client,
  ClientFormData,
} from "@/types/stock";
import {
  EnhancedInvoice,
  PaymentRecord,
  PaymentRecordFormData,
  PaymentSummary,
  PaymentAnalytics,
  InvoiceTemplate,
  InvoiceTemplateFormData,
} from "@/types/invoice";
import { PaymentService } from "@/services/PaymentService";
import { TemplateService } from "@/services/TemplateService";
import { InvoiceCreditService } from "@/services/InvoiceCreditService";
import { CreditManagementService } from "@/services/CreditManagementService";

interface EnhancedInvoiceContextType {
  // Invoices
  invoices: Invoice[];
  enhancedInvoices: EnhancedInvoice[];
  clients: Client[];
  templates: InvoiceTemplate[];
  loading: boolean;

  // Invoice operations
  createInvoice: (data: InvoiceFormData) => Promise<Invoice>;
  updateInvoice: (id: string, data: Partial<InvoiceFormData>) => Promise<void>;
  deleteInvoice: (id: string) => Promise<void>;
  getInvoiceById: (id: string) => Promise<Invoice | undefined>;
  getEnhancedInvoiceById: (id: string) => Promise<EnhancedInvoice | undefined>;

  // Client operations
  createClient: (data: ClientFormData) => Promise<Client>;
  updateClient: (id: string, data: Partial<ClientFormData>) => Promise<void>;
  deleteClient: (id: string) => Promise<void>;

  // Template operations
  createTemplate: (data: InvoiceTemplateFormData) => Promise<InvoiceTemplate>;
  updateTemplate: (
    id: string,
    data: Partial<InvoiceTemplateFormData>
  ) => Promise<void>;
  deleteTemplate: (id: string) => Promise<void>;
  getTemplate: (id: string) => Promise<InvoiceTemplate | null>;
  getDefaultTemplate: () => Promise<InvoiceTemplate | null>;
  cloneTemplate: (
    templateId: string,
    newName: string,
    setAsDefault?: boolean
  ) => Promise<InvoiceTemplate>;
  setTemplateAsDefault: (templateId: string) => Promise<void>;
  setTemplateStatus: (templateId: string, isActive: boolean) => Promise<void>;
  getTemplateUsageStats: (templateId: string) => Promise<{
    invoiceCount: number;
    lastUsed?: Date;
    totalRevenue: number;
  }>;

  // Payment operations
  createPaymentRecord: (data: PaymentRecordFormData) => Promise<PaymentRecord>;
  updatePaymentRecord: (
    id: string,
    data: Partial<PaymentRecordFormData>
  ) => Promise<void>;
  deletePaymentRecord: (id: string) => Promise<void>;
  getPaymentRecordsForInvoice: (invoiceId: string) => Promise<PaymentRecord[]>;
  getPaymentSummary: (invoiceId: string) => Promise<PaymentSummary>;

  // Analytics and reporting
  getPaymentAnalytics: (
    startDate?: Date,
    endDate?: Date
  ) => Promise<PaymentAnalytics>;
  getOverdueInvoices: () => Promise<EnhancedInvoice[]>;
  updateOverdueInvoices: () => Promise<number>;

  // Refresh functions
  refreshInvoices: () => Promise<void>;
  refreshClients: () => Promise<void>;
  refreshTemplates: () => Promise<void>;
  refreshPaymentData: (invoiceId?: string) => Promise<void>;
}

const EnhancedInvoiceContext = createContext<
  EnhancedInvoiceContextType | undefined
>(undefined);

export const useEnhancedInvoices = () => {
  const context = useContext(EnhancedInvoiceContext);
  if (!context) {
    throw new Error(
      "useEnhancedInvoices must be used within an EnhancedInvoiceProvider"
    );
  }
  return context;
};

interface EnhancedInvoiceProviderProps {
  children: React.ReactNode;
}

export const EnhancedInvoiceProvider: React.FC<
  EnhancedInvoiceProviderProps
> = ({ children }) => {
  const { user } = useAuth();
  const { repairShop } = useRepairShopContext();
  const { t } = useTranslation();

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [enhancedInvoices, setEnhancedInvoices] = useState<EnhancedInvoice[]>(
    []
  );
  const [clients, setClients] = useState<Client[]>([]);
  const [templates, setTemplates] = useState<InvoiceTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && repairShop) {
      refreshAll();
      setupRealtimeSubscriptions();
    } else {
      setInvoices([]);
      setEnhancedInvoices([]);
      setClients([]);
      setTemplates([]);
      setLoading(false);
    }
  }, [user, repairShop]);

  const refreshAll = async () => {
    try {
      await Promise.all([
        refreshClients(),
        refreshInvoices(),
        refreshEnhancedInvoices(),
        refreshTemplates(),
      ]);
    } catch (error) {
      console.error("Error refreshing invoice data:", error);
    }
    setLoading(false);
  };

  const setupRealtimeSubscriptions = () => {
    if (!repairShop) return;

    // Invoices subscription
    const invoicesSubscription = supabase
      .channel("invoices-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "invoices",
          filter: `repair_shop_id=eq.${repairShop.id}`,
        },
        () => {
          refreshInvoices();
          refreshEnhancedInvoices();
        }
      )
      .subscribe();

    // Payment records subscription
    const paymentsSubscription = supabase
      .channel("payments-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "payment_records",
          filter: `repair_shop_id=eq.${repairShop.id}`,
        },
        () => refreshEnhancedInvoices()
      )
      .subscribe();

    // Clients subscription
    const clientsSubscription = supabase
      .channel("clients-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "clients",
          filter: `repair_shop_id=eq.${repairShop.id}`,
        },
        () => refreshClients()
      )
      .subscribe();

    // Templates subscription
    const templatesSubscription = supabase
      .channel("templates-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "invoice_templates",
          filter: `repair_shop_id=eq.${repairShop.id}`,
        },
        () => refreshTemplates()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(invoicesSubscription);
      supabase.removeChannel(paymentsSubscription);
      supabase.removeChannel(clientsSubscription);
      supabase.removeChannel(templatesSubscription);
    };
  };

  const refreshClients = async () => {
    if (!user || !repairShop) return;

    try {
      const { data, error } = await supabase
        .from("clients")
        .select("*")
        .eq("repair_shop_id", repairShop.id)
        .order("name");

      if (error) throw error;

      const processedClients: Client[] = data.map((client) => ({
        id: client.id,
        name: client.name,
        phone: client.phone || undefined,
        email: client.email || undefined,
        address: client.address || undefined,
        notes: client.notes || undefined,
        creditLimit: client.credit_limit,
        currentCreditBalance: client.current_credit_balance,
        isActive: client.is_active,
        repairShopId: client.repair_shop_id,
        createdAt: new Date(client.created_at),
        updatedAt: new Date(client.updated_at),
      }));

      setClients(processedClients);
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error(t("invoices.fetchError"));
    }
  };

  const refreshInvoices = async () => {
    if (!user || !repairShop) return;

    try {
      const { data, error } = await supabase
        .from("invoices")
        .select(
          `
          *,
          client:clients(*),
          items:invoice_items(*)
        `
        )
        .eq("repair_shop_id", repairShop.id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      const processedInvoices: Invoice[] = data.map((invoice) => ({
        id: invoice.id,
        invoiceNumber: invoice.invoice_number,
        clientId: invoice.client_id || undefined,
        client: invoice.client
          ? {
              id: invoice.client.id,
              name: invoice.client.name,
              phone: invoice.client.phone || undefined,
              email: invoice.client.email || undefined,
              address: invoice.client.address || undefined,
              notes: invoice.client.notes || undefined,
              creditLimit: invoice.client.credit_limit,
              currentCreditBalance: invoice.client.current_credit_balance,
              isActive: invoice.client.is_active,
              repairShopId: invoice.client.repair_shop_id,
              createdAt: new Date(invoice.client.created_at),
              updatedAt: new Date(invoice.client.updated_at),
            }
          : undefined,
        clientName: invoice.client_name,
        clientPhone: invoice.client_phone || undefined,
        clientEmail: invoice.client_email || undefined,
        clientAddress: invoice.client_address || undefined,
        subtotal: invoice.subtotal,
        taxRate: invoice.tax_rate,
        taxAmount: invoice.tax_amount,
        discountAmount: invoice.discount_amount,
        totalAmount: invoice.total_amount,
        status: invoice.status as Invoice["status"],
        dueDate: invoice.due_date ? new Date(invoice.due_date) : undefined,
        notes: invoice.notes || undefined,
        termsAndConditions: invoice.terms_and_conditions || undefined,
        repairShopId: invoice.repair_shop_id,
        createdBy: invoice.created_by,
        createdAt: new Date(invoice.created_at),
        updatedAt: new Date(invoice.updated_at),
        items: invoice.items.map((item: any) => ({
          id: item.id,
          invoiceId: item.invoice_id,
          productId: item.product_id || undefined,
          productName: item.product_name,
          description: item.description || undefined,
          quantity: item.quantity,
          unitPrice: item.unit_price,
          discountPercentage: item.discount_percentage,
          discountAmount: item.discount_amount,
          totalPrice: item.total_price,
          createdAt: new Date(item.created_at),
          updatedAt: new Date(item.updated_at),
        })),
      }));

      setInvoices(processedInvoices);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast.error(t("invoices.fetchError"));
    }
  };

  const refreshEnhancedInvoices = async () => {
    if (!user || !repairShop) return;

    try {
      const { data, error } = await supabase
        .from("invoices")
        .select(
          `
          *,
          client:clients(*),
          items:invoice_items(*),
          payment_records(*)
        `
        )
        .eq("repair_shop_id", repairShop.id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      const processedInvoices: EnhancedInvoice[] = data.map((invoice) => ({
        id: invoice.id,
        invoiceNumber: invoice.invoice_number,
        clientId: invoice.client_id || undefined,
        client: invoice.client
          ? {
              id: invoice.client.id,
              name: invoice.client.name,
              phone: invoice.client.phone || undefined,
              email: invoice.client.email || undefined,
              address: invoice.client.address || undefined,
              notes: invoice.client.notes || undefined,
              creditLimit: invoice.client.credit_limit,
              currentCreditBalance: invoice.client.current_credit_balance,
              isActive: invoice.client.is_active,
              repairShopId: invoice.client.repair_shop_id,
              createdAt: new Date(invoice.client.created_at),
              updatedAt: new Date(invoice.client.updated_at),
            }
          : undefined,
        clientName: invoice.client_name,
        clientPhone: invoice.client_phone || undefined,
        clientEmail: invoice.client_email || undefined,
        clientAddress: invoice.client_address || undefined,
        subtotal: invoice.subtotal,
        taxRate: invoice.tax_rate,
        taxAmount: invoice.tax_amount,
        discountAmount: invoice.discount_amount,
        totalAmount: invoice.total_amount,
        status: invoice.status as EnhancedInvoice["status"],

        // Enhanced payment fields
        paymentStatus: invoice.payment_status || "unpaid",
        paidAmount: invoice.paid_amount || 0,
        remainingAmount: invoice.remaining_amount || invoice.total_amount,
        lastPaymentDate: invoice.last_payment_date
          ? new Date(invoice.last_payment_date)
          : undefined,
        templateId: invoice.template_id,

        dueDate: invoice.due_date ? new Date(invoice.due_date) : undefined,
        notes: invoice.notes || undefined,
        termsAndConditions: invoice.terms_and_conditions || undefined,
        repairShopId: invoice.repair_shop_id,
        createdBy: invoice.created_by,
        createdAt: new Date(invoice.created_at),
        updatedAt: new Date(invoice.updated_at),

        items: invoice.items.map((item: any) => ({
          id: item.id,
          invoiceId: item.invoice_id,
          productId: item.product_id || undefined,
          productName: item.product_name,
          description: item.description || undefined,
          quantity: item.quantity,
          unitPrice: item.unit_price,
          discountPercentage: item.discount_percentage,
          discountAmount: item.discount_amount,
          totalPrice: item.total_price,
          createdAt: new Date(item.created_at),
          updatedAt: new Date(item.updated_at),
        })),

        // Payment records
        paymentRecords:
          invoice.payment_records?.map((payment: any) => ({
            id: payment.id,
            invoiceId: payment.invoice_id,
            paymentDate: new Date(payment.payment_date),
            amount: payment.amount,
            paymentMethod: payment.payment_method,
            referenceNumber: payment.reference_number,
            notes: payment.notes,
            repairShopId: payment.repair_shop_id,
            createdBy: payment.created_by,
            createdAt: new Date(payment.created_at),
            updatedAt: new Date(payment.updated_at),
          })) || [],

        // Calculated fields
        daysOverdue: invoice.due_date
          ? Math.max(
              0,
              Math.ceil(
                (new Date().getTime() - new Date(invoice.due_date).getTime()) /
                  (1000 * 60 * 60 * 24)
              )
            )
          : 0,
        isOverdue: invoice.due_date
          ? new Date() > new Date(invoice.due_date) &&
            invoice.payment_status !== "paid"
          : false,
      }));

      setEnhancedInvoices(processedInvoices);
    } catch (error) {
      console.error("Error fetching enhanced invoices:", error);
      toast.error(t("invoices.fetchError"));
    }
  };

  // Client CRUD operations
  const createClient = async (data: ClientFormData): Promise<Client> => {
    if (!user || !repairShop) throw new Error("User or repair shop not found");

    try {
      const { data: insertedData, error } = await supabase
        .from("clients")
        .insert({
          name: data.name,
          phone: data.phone,
          email: data.email,
          address: data.address,
          notes: data.notes,
          credit_limit: data.creditLimit,
          is_active: data.isActive,
          repair_shop_id: repairShop.id,
        })
        .select()
        .single();

      if (error) throw error;

      const newClient: Client = {
        id: insertedData.id,
        name: insertedData.name,
        phone: insertedData.phone || undefined,
        email: insertedData.email || undefined,
        address: insertedData.address || undefined,
        notes: insertedData.notes || undefined,
        creditLimit: insertedData.credit_limit,
        currentCreditBalance: insertedData.current_credit_balance,
        isActive: insertedData.is_active,
        repairShopId: insertedData.repair_shop_id,
        createdAt: new Date(insertedData.created_at),
        updatedAt: new Date(insertedData.updated_at),
      };

      setClients([...clients, newClient]);
      return newClient;
    } catch (error) {
      console.error("Error creating client:", error);
      throw error;
    }
  };

  const updateClient = async (
    id: string,
    data: Partial<ClientFormData>
  ): Promise<void> => {
    try {
      const { error } = await supabase
        .from("clients")
        .update({
          name: data.name,
          phone: data.phone,
          email: data.email,
          address: data.address,
          notes: data.notes,
          credit_limit: data.creditLimit,
          is_active: data.isActive,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id);

      if (error) throw error;

      await refreshClients();
    } catch (error) {
      console.error("Error updating client:", error);
      throw error;
    }
  };

  const deleteClient = async (id: string): Promise<void> => {
    try {
      const { error } = await supabase.from("clients").delete().eq("id", id);

      if (error) throw error;

      setClients(clients.filter((c) => c.id !== id));
    } catch (error) {
      console.error("Error deleting client:", error);
      throw error;
    }
  };

  const refreshTemplates = async () => {
    if (!user || !repairShop) return;

    try {
      const fetchedTemplates = await TemplateService.getTemplates(
        repairShop.id
      );
      setTemplates(fetchedTemplates);
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error(t("invoices.templateFetchError"));
    }
  };

  // Template CRUD operations
  const createTemplate = async (
    data: InvoiceTemplateFormData
  ): Promise<InvoiceTemplate> => {
    if (!user || !repairShop) throw new Error("User or repair shop not found");

    try {
      const newTemplate = await TemplateService.createTemplate(data);
      await refreshTemplates();
      toast.success(t("invoices.templateCreated"));
      return newTemplate;
    } catch (error) {
      console.error("Error creating template:", error);
      toast.error(t("invoices.templateCreateError"));
      throw error;
    }
  };

  const updateTemplate = async (
    id: string,
    data: Partial<InvoiceTemplateFormData>
  ): Promise<void> => {
    try {
      await TemplateService.updateTemplate(id, data);
      await refreshTemplates();
      toast.success(t("invoices.templateUpdated"));
    } catch (error) {
      console.error("Error updating template:", error);
      toast.error(t("invoices.templateUpdateError"));
      throw error;
    }
  };

  const deleteTemplate = async (id: string): Promise<void> => {
    try {
      await TemplateService.deleteTemplate(id);
      await refreshTemplates();
      toast.success(t("invoices.templateDeleted"));
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error(t("invoices.templateDeleteError"));
      throw error;
    }
  };

  const getTemplate = async (id: string): Promise<InvoiceTemplate | null> => {
    try {
      return await TemplateService.getTemplate(id);
    } catch (error) {
      console.error("Error fetching template:", error);
      throw error;
    }
  };

  const getDefaultTemplate = async (): Promise<InvoiceTemplate | null> => {
    if (!repairShop) throw new Error("Repair shop not found");

    try {
      return await TemplateService.getDefaultTemplate(repairShop.id);
    } catch (error) {
      console.error("Error fetching default template:", error);
      throw error;
    }
  };

  const cloneTemplate = async (
    templateId: string,
    newName: string,
    setAsDefault: boolean = false
  ): Promise<InvoiceTemplate> => {
    try {
      const clonedTemplate = await TemplateService.cloneTemplate(
        templateId,
        newName,
        setAsDefault
      );
      await refreshTemplates();
      toast.success(t("invoices.templateCloned"));
      return clonedTemplate;
    } catch (error) {
      console.error("Error cloning template:", error);
      toast.error(t("invoices.templateCloneError"));
      throw error;
    }
  };

  const setTemplateAsDefault = async (templateId: string): Promise<void> => {
    try {
      await TemplateService.setAsDefault(templateId);
      await refreshTemplates();
      toast.success(t("invoices.templateSetAsDefault"));
    } catch (error) {
      console.error("Error setting template as default:", error);
      toast.error(t("invoices.templateSetDefaultError"));
      throw error;
    }
  };

  const setTemplateStatus = async (
    templateId: string,
    isActive: boolean
  ): Promise<void> => {
    try {
      await TemplateService.setTemplateStatus(templateId, isActive);
      await refreshTemplates();
      toast.success(
        isActive
          ? t("invoices.templateActivated")
          : t("invoices.templateDeactivated")
      );
    } catch (error) {
      console.error("Error updating template status:", error);
      toast.error(t("invoices.templateStatusError"));
      throw error;
    }
  };

  const getTemplateUsageStats = async (
    templateId: string
  ): Promise<{
    invoiceCount: number;
    lastUsed?: Date;
    totalRevenue: number;
  }> => {
    try {
      return await TemplateService.getTemplateUsageStats(templateId);
    } catch (error) {
      console.error("Error fetching template usage stats:", error);
      throw error;
    }
  };

  // Invoice CRUD operations
  const createInvoice = async (data: InvoiceFormData): Promise<Invoice> => {
    if (!user || !repairShop) throw new Error("User or repair shop not found");

    try {
      // Generate invoice number
      const { data: invoiceNumberData } = await supabase.rpc(
        "generate_invoice_number",
        { shop_id: repairShop.id }
      );

      const invoiceNumber = invoiceNumberData || `INV-${Date.now()}`;

      // Calculate totals
      const subtotal = data.items.reduce((sum, item) => {
        const itemTotal = item.quantity * item.unitPrice;
        const discount = (itemTotal * item.discountPercentage) / 100;
        return sum + (itemTotal - discount);
      }, 0);

      const taxAmount = (subtotal * data.taxRate) / 100;
      const totalAmount = subtotal + taxAmount - data.discountAmount;

      // Check credit limit if client is specified
      if (data.clientId && data.clientId.trim() !== "") {
        const creditValidation =
          await CreditManagementService.validateCreditLimit(
            data.clientId,
            totalAmount
          );
        if (!creditValidation.canCreate) {
          throw new Error(creditValidation.reason || "Credit limit exceeded");
        }
      }

      const { data: insertedData, error } = await supabase
        .from("invoices")
        .insert({
          invoice_number: invoiceNumber,
          client_id: data.clientId || null, // Convert empty string to null
          client_name: data.clientName,
          client_phone: data.clientPhone,
          client_email: data.clientEmail,
          client_address: data.clientAddress,
          subtotal,
          tax_rate: data.taxRate,
          tax_amount: taxAmount,
          discount_amount: data.discountAmount,
          total_amount: totalAmount,
          payment_status: "unpaid", // Set default payment status to unpaid
          due_date: data.dueDate?.toISOString(),
          notes: data.notes,
          terms_and_conditions: data.termsAndConditions,
          template_id: data.templateId || null, // Convert empty string to null
          repair_shop_id: repairShop.id,
          created_by: user.id,
        })
        .select()
        .single();

      if (error) throw error;

      // Insert invoice items
      if (data.items.length > 0) {
        const itemsToInsert = data.items.map((item) => ({
          invoice_id: insertedData.id,
          product_id: item.productId || null, // Convert empty string to null
          product_name: item.productName,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unitPrice,
          discount_percentage: item.discountPercentage,
          discount_amount:
            (item.quantity * item.unitPrice * item.discountPercentage) / 100,
          total_price:
            item.quantity * item.unitPrice -
            (item.quantity * item.unitPrice * item.discountPercentage) / 100,
        }));

        const { error: itemsError } = await supabase
          .from("invoice_items")
          .insert(itemsToInsert);

        if (itemsError) throw itemsError;
      }

      // Update client credit if applicable
      if (data.clientId && data.clientId.trim() !== "") {
        console.log(
          "EnhancedInvoiceContext: Updating credit for client",
          data.clientId,
          "with amount",
          totalAmount
        );
        try {
          await CreditManagementService.recalculateClientCredit(data.clientId);
          console.log("EnhancedInvoiceContext: Credit update completed");
        } catch (creditError) {
          console.error("Error updating client credit balance:", creditError);
          // Don't fail the invoice creation, but log the error
        }
      } else {
        console.log(
          "EnhancedInvoiceContext: No client ID or empty client ID, skipping credit update"
        );
      }

      await refreshInvoices();
      await refreshEnhancedInvoices();
      return insertedData as any;
    } catch (error) {
      console.error("Error creating invoice:", error);
      throw error;
    }
  };

  const updateInvoice = async (
    id: string,
    data: Partial<InvoiceFormData>
  ): Promise<void> => {
    try {
      // Calculate totals if items are provided
      let updateData: any = {
        client_id: data.clientId || null, // Convert empty string to null
        client_name: data.clientName,
        client_phone: data.clientPhone,
        client_email: data.clientEmail,
        client_address: data.clientAddress,
        tax_rate: data.taxRate,
        discount_amount: data.discountAmount,
        due_date: data.dueDate?.toISOString(),
        notes: data.notes,
        terms_and_conditions: data.termsAndConditions,
        template_id: data.templateId || null, // Convert empty string to null
        updated_at: new Date().toISOString(),
      };

      if (data.items) {
        const subtotal = data.items.reduce((sum, item) => {
          const itemTotal = item.quantity * item.unitPrice;
          const discount = (itemTotal * item.discountPercentage) / 100;
          return sum + (itemTotal - discount);
        }, 0);

        const taxAmount = (subtotal * (data.taxRate || 0)) / 100;
        const totalAmount = subtotal + taxAmount - (data.discountAmount || 0);

        updateData = {
          ...updateData,
          subtotal,
          tax_amount: taxAmount,
          total_amount: totalAmount,
        };
      }

      const { error } = await supabase
        .from("invoices")
        .update(updateData)
        .eq("id", id);

      if (error) throw error;

      // Update invoice items if provided
      if (data.items) {
        // Delete existing items
        await supabase.from("invoice_items").delete().eq("invoice_id", id);

        // Insert new items
        if (data.items.length > 0) {
          const itemsToInsert = data.items.map((item) => ({
            invoice_id: id,
            product_id: item.productId || null, // Convert empty string to null
            product_name: item.productName,
            description: item.description,
            quantity: item.quantity,
            unit_price: item.unitPrice,
            discount_percentage: item.discountPercentage,
            discount_amount:
              (item.quantity * item.unitPrice * item.discountPercentage) / 100,
            total_price:
              item.quantity * item.unitPrice -
              (item.quantity * item.unitPrice * item.discountPercentage) / 100,
          }));

          const { error: itemsError } = await supabase
            .from("invoice_items")
            .insert(itemsToInsert);

          if (itemsError) throw itemsError;
        }
      }

      await refreshInvoices();
      await refreshEnhancedInvoices();
    } catch (error) {
      console.error("Error updating invoice:", error);
      throw error;
    }
  };

  const deleteInvoice = async (id: string): Promise<void> => {
    try {
      const { error } = await supabase.from("invoices").delete().eq("id", id);

      if (error) throw error;

      setInvoices(invoices.filter((i) => i.id !== id));
      setEnhancedInvoices(enhancedInvoices.filter((i) => i.id !== id));
    } catch (error) {
      console.error("Error deleting invoice:", error);
      throw error;
    }
  };

  const getInvoiceById = async (id: string): Promise<Invoice | undefined> => {
    return invoices.find((invoice) => invoice.id === id);
  };

  const getEnhancedInvoiceById = async (
    id: string
  ): Promise<EnhancedInvoice | undefined> => {
    return enhancedInvoices.find((invoice) => invoice.id === id);
  };

  // Payment operations
  const createPaymentRecord = async (
    data: PaymentRecordFormData
  ): Promise<PaymentRecord> => {
    if (!user || !repairShop) throw new Error("User or repair shop not found");

    try {
      const paymentRecord = await PaymentService.createPaymentRecord(
        data,
        repairShop.id,
        user.id
      );

      // Refresh enhanced invoices to get updated payment status
      await refreshEnhancedInvoices();

      toast.success(t("invoices.paymentRecorded"));
      return paymentRecord;
    } catch (error) {
      console.error("Error creating payment record:", error);
      toast.error(t("invoices.paymentRecordError"));
      throw error;
    }
  };

  const updatePaymentRecord = async (
    id: string,
    data: Partial<PaymentRecordFormData>
  ): Promise<void> => {
    try {
      await PaymentService.updatePaymentRecord(id, data);

      // Refresh enhanced invoices to get updated payment status
      await refreshEnhancedInvoices();

      toast.success(t("invoices.paymentUpdated"));
    } catch (error) {
      console.error("Error updating payment record:", error);
      toast.error(t("invoices.paymentUpdateError"));
      throw error;
    }
  };

  const deletePaymentRecord = async (id: string): Promise<void> => {
    try {
      await PaymentService.deletePaymentRecord(id);

      // Refresh enhanced invoices to get updated payment status
      await refreshEnhancedInvoices();

      toast.success(t("invoices.paymentDeleted"));
    } catch (error) {
      console.error("Error deleting payment record:", error);
      toast.error(t("invoices.paymentDeleteError"));
      throw error;
    }
  };

  const getPaymentRecordsForInvoice = async (
    invoiceId: string
  ): Promise<PaymentRecord[]> => {
    try {
      return await PaymentService.getPaymentRecordsForInvoice(invoiceId);
    } catch (error) {
      console.error("Error fetching payment records:", error);
      throw error;
    }
  };

  const getPaymentSummary = async (
    invoiceId: string
  ): Promise<PaymentSummary> => {
    try {
      return await PaymentService.getPaymentSummary(invoiceId);
    } catch (error) {
      console.error("Error fetching payment summary:", error);
      throw error;
    }
  };

  // Analytics and reporting
  const getPaymentAnalytics = async (
    startDate?: Date,
    endDate?: Date
  ): Promise<PaymentAnalytics> => {
    if (!repairShop) throw new Error("Repair shop not found");

    try {
      return await PaymentService.getPaymentAnalytics(
        repairShop.id,
        startDate,
        endDate
      );
    } catch (error) {
      console.error("Error fetching payment analytics:", error);
      throw error;
    }
  };

  const getOverdueInvoices = async (): Promise<EnhancedInvoice[]> => {
    if (!repairShop) throw new Error("Repair shop not found");

    try {
      return await PaymentService.getOverdueInvoices(repairShop.id);
    } catch (error) {
      console.error("Error fetching overdue invoices:", error);
      throw error;
    }
  };

  const updateOverdueInvoices = async (): Promise<number> => {
    try {
      const updatedCount = await PaymentService.updateOverdueInvoices();

      // Refresh enhanced invoices to get updated statuses
      await refreshEnhancedInvoices();

      return updatedCount;
    } catch (error) {
      console.error("Error updating overdue invoices:", error);
      throw error;
    }
  };

  const refreshPaymentData = async (invoiceId?: string): Promise<void> => {
    try {
      if (invoiceId) {
        // Refresh specific invoice payment data
        await PaymentService.recalculateInvoicePaymentStatus(invoiceId);
      }

      // Refresh all enhanced invoices
      await refreshEnhancedInvoices();
    } catch (error) {
      console.error("Error refreshing payment data:", error);
      throw error;
    }
  };

  return (
    <EnhancedInvoiceContext.Provider
      value={{
        invoices,
        enhancedInvoices,
        clients,
        templates,
        loading,
        createInvoice,
        updateInvoice,
        deleteInvoice,
        getInvoiceById,
        getEnhancedInvoiceById,
        createClient,
        updateClient,
        deleteClient,
        createTemplate,
        updateTemplate,
        deleteTemplate,
        getTemplate,
        getDefaultTemplate,
        cloneTemplate,
        setTemplateAsDefault,
        setTemplateStatus,
        getTemplateUsageStats,
        createPaymentRecord,
        updatePaymentRecord,
        deletePaymentRecord,
        getPaymentRecordsForInvoice,
        getPaymentSummary,
        getPaymentAnalytics,
        getOverdueInvoices,
        updateOverdueInvoices,
        refreshInvoices,
        refreshClients,
        refreshTemplates,
        refreshPaymentData,
      }}
    >
      {children}
    </EnhancedInvoiceContext.Provider>
  );
};

export default EnhancedInvoiceProvider;
